// Keep checkout behind a confirmed cart, including while sections are replaced.
// Product forms install the same guard when this cart asset is not loaded.
if (!window.WorthItCartCheckout) {
  const owners = new Map();
  const buttons = new WeakMap();
  const widgets = new WeakMap();
  const sync = () => {
    const locked = owners.size > 0;
    const uncertain = [...owners.values()].some(Boolean);
    document.querySelectorAll('button[name="checkout"], input[name="checkout"]').forEach((button) => {
      if (locked) {
        if (!buttons.has(button)) buttons.set(button, button.disabled);
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        let status = button.parentElement.querySelector('[data-wi-cart-checkout-status]');
        if (!status) {
          status = document.createElement('p');
          status.setAttribute('data-wi-cart-checkout-status', '');
          status.setAttribute('role', 'status');
          status.style.cssText = 'margin:8px 0 0;font-size:14px;line-height:1.4;';
          button.parentElement.appendChild(status);
        }
        const message = uncertain ? 'Refresh your cart display before checkout.' : 'Updating your cart…';
        if (status.textContent !== message) status.textContent = message;
      } else if (buttons.has(button)) {
        button.disabled = buttons.get(button);
        buttons.delete(button);
        button.removeAttribute('aria-busy');
      }
    });
    document.querySelectorAll('.cart__dynamic-checkout-buttons, .shopify-payment-button').forEach((widget) => {
      if (locked) {
        if (!widgets.has(widget)) widgets.set(widget, widget.inert);
        widget.inert = true;
      } else if (widgets.has(widget)) {
        widget.inert = widgets.get(widget);
        widgets.delete(widget);
      }
    });
    if (!locked) document.querySelectorAll('[data-wi-cart-checkout-status]').forEach((status) => status.remove());
  };
  const block = (event) => {
    if (!owners.size) return;
    const cartForm = event.type === 'submit' && ['cart', 'CartDrawer-Form'].includes(event.target.id);
    const checkoutControl = event.type === 'click' && event.target.closest?.('button[name="checkout"], input[name="checkout"], .cart__dynamic-checkout-buttons, .shopify-payment-button');
    const link = event.type === 'click' && event.target.closest?.('a[href]');
    const checkoutLink = link && /^\/checkouts?(?:\/|$)/.test(new URL(link.href, window.location.origin).pathname);
    if (cartForm || checkoutControl || checkoutLink) {
      event.preventDefault();
      event.stopImmediatePropagation();
      sync();
    }
  };
  document.addEventListener('submit', block, true);
  document.addEventListener('click', block, true);
  new MutationObserver(sync).observe(document.documentElement, { childList: true, subtree: true });
  window.WorthItCartCheckout = {
    lock(owner) { owners.set(owner, false); sync(); },
    uncertain(owner) { owners.set(owner, true); sync(); },
    confirm(owner, cart) {
      if (!cart || typeof cart.currency !== 'string' || !cart.currency || !Array.isArray(cart.items) ||
          !Number.isInteger(cart.item_count) || cart.item_count < 0 ||
          !cart.items.every((item) => typeof item.key === 'string' && item.key && Number.isInteger(item.quantity) && item.quantity > 0) ||
          cart.items.reduce((count, item) => count + item.quantity, 0) !== cart.item_count) return false;
      owners.delete(owner);
      sync();
      return true;
    },
  };
}

// Either asset can load first (including product forms without a drawer).
// The deadline settles locally even when fetch/body ignores a best-effort abort.
if (!window.WorthItCartRequest) {
  window.WorthItCartRequest = (url, config = {}, bodyMethod = 'json') => new Promise((resolve, reject) => {
    const controller = new AbortController();
    let settled = false;
    const settle = (error, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(deadline);
      if (error) reject(error);
      else resolve(value);
    };
    const deadline = setTimeout(() => {
      const error = new Error('The cart request took too long to confirm');
      error.code = 'CART_REQUEST_TIMEOUT';
      settle(error);
      controller.abort();
    }, 10000);
    // The body is inside the same deadline, rather than a new timer after fetch.
    try {
      Promise.resolve(fetch(url, { ...config, signal: controller.signal }))
        .then(async (response) => ({ response, data: await response[bodyMethod]() }))
        .then((value) => settle(null, value), (error) => settle(error));
    } catch (error) { settle(error); }
  });
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        // One shared owner covers in-flight mutations and GET-only recovery.
        const owner = ProductForm.pendingCartRefresh ||
          (typeof CartItems !== 'undefined' && CartItems.getPendingOwner?.());
        if (owner) return owner.refreshCartDisplay();
        if (!this.isConnected) return Promise.resolve(false);
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        this.handleErrorMessage();
        this.error = false;
        this.pendingCartOutcome = null;
        this.pendingCartWarning = null;
        this.cartWasOpen = this.cart?.classList.contains('active');
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;
        const variantId = formData.get('id');
        const deferred = this.createCartLinesUpdateEvent(variantId, parseInt(formData.get('quantity')) || 1);
        ProductForm.pendingCartRefresh = this;
        window.WorthItCartCheckout.lock(this);
        if (typeof CartItems !== 'undefined') ++CartItems.displayGeneration;
        let accepted = false;
        let eventSettled = false;
        let acceptedState;
        const reject = (error) => {
          if (eventSettled) return;
          eventSettled = true;
          deferred?.reject(error);
        };
        this.pendingAddPromise = window.WorthItCartRequest(routes.cart_add_url, config)
          .then(({ response, data }) => ({ state: data, status: response.status, ok: response.ok }))
          .then(async ({ state, status, ok }) => {
            if (state.status || ok === false) {
              const message = state.description || state.message || 'Shopify could not add the requested quantity.';
              reject(new Error(message));
              this.dispatchCartErrorEvent(message, 'INVALID');
              try {
                Promise.resolve(publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form', productVariantId: variantId,
                  errors: state.errors || state.description, message: state.message,
                })).catch((error) => console.error(error));
              } catch (error) { console.error(error); }
              // Add 422 can add the maximum available quantity before reporting
              // insufficient stock. Reconcile the genuine cart; never retry Add.
              this.pendingCartOutcome = status === 422 || state.status === 422 ? 'quantity-warning' : 'unconfirmed';
              this.pendingCartWarning = this.pendingCartOutcome === 'quantity-warning'
                ? `${message} Review the actual quantity in your cart before trying again.`
                : 'We could not confirm this addition. Check your cart before trying again.';
              this.handleErrorMessage(this.pendingCartWarning);
              this.pendingCartState = { id: variantId };
              this.showCartRecovery();
              return this.refreshCartDisplay(true);
            }
            if (!state.id && !state.items?.length) throw new Error('Unrecognised cart response');
            accepted = true;
            acceptedState = state;
            this.pendingCartOutcome = 'accepted';
            // Wait for fresh StandardEvent aggregates before releasing the guard.
            const confirmedCart = await this.resolveCartLinesUpdate(deferred);
            eventSettled = true;
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }
            try {
              if (!confirmedCart) throw new Error('The current cart could not be confirmed');
              await this.renderCartDisplay(state);
              window.WorthItCartCheckout.confirm(this, confirmedCart);
              this.publishCartUpdate(state, variantId);
              document.dispatchEvent(new CustomEvent('worthit:cart-added', { detail: { variantId } }));
            } catch (error) {
              console.error(error);
              this.pendingCartState = state;
              this.showCartRecovery();
              return this.refreshCartDisplay(true);
            }
          })
          .catch((error) => {
            console.error(error);
            if (accepted) {
              this.pendingCartOutcome = 'accepted';
              this.pendingCartState = acceptedState;
            } else {
              reject(error);
              this.pendingCartOutcome = 'unconfirmed';
              this.pendingCartWarning = 'We could not confirm this addition. Check your cart before trying again.';
              this.handleErrorMessage(this.pendingCartWarning);
              this.dispatchCartErrorEvent(this.pendingCartWarning, 'SERVICE_UNAVAILABLE');
              this.pendingCartState = { id: variantId };
            }
            this.showCartRecovery();
            return this.refreshCartDisplay(true);
          })
          .finally(() => {
            this.pendingAddPromise = null;
            if (!this.pendingCartState && ProductForm.pendingCartRefresh === this) ProductForm.pendingCartRefresh = null;
            this.submitButton.classList.remove('loading');
            if (!this.pendingCartState) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            CartPerformance.measureFromEvent('add:user-action', evt);
          });
        return this.pendingAddPromise;
      }

      publishCartUpdate(state, variantId) {
        // Publish only after paint/reconciliation, when subscribers can refresh
        // their own page sections without waiting on this same mutation owner.
        if (ProductForm.pendingCartRefresh === this && !this.pendingCartState) ProductForm.pendingCartRefresh = null;
        try {
          const marker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
          Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form', productVariantId: variantId, cartData: state,
          })).then(() => CartPerformance.measureFromMarker('add:wait-for-subscribers', marker))
            .catch((error) => console.error(error));
        } catch (error) { console.error(error); }
      }

      async renderCartDisplay(state) {
        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal?.hasAttribute('open')) {
          await new Promise((resolve) => {
            document.body.addEventListener('modalClosed', () => setTimeout(resolve), { once: true });
            quickAddModal.hide(true);
          });
        }

        // Bundled section rendering can return null after a successful Add.
        // Validate all fragments before any native renderer mutates the DOM.
        this.cart.getSectionsToRender().forEach((section) => {
          const target = section.selector && section.id !== 'cart-notification-product'
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          const html = state.sections?.[section.id];
          const selector = section.id === 'cart-notification-product'
            ? `[id="cart-notification-product-${state.key}"]`
            : section.selector || '.shopify-section';
          const fragment = typeof html === 'string'
            ? new DOMParser().parseFromString(html, 'text/html').querySelector(selector)
            : null;
          if (!target || !fragment || (section.id === 'cart-drawer' && (
            !fragment.querySelector('#CartDrawer-Overlay') ||
            !fragment.querySelector('.drawer__inner') ||
            !fragment.querySelector('.drawer__close') ||
            !fragment.querySelector('cart-drawer-items') ||
            (state.item_count === 0 && !fragment.querySelector('.drawer__inner-empty'))
          ))) {
            throw new Error('The cart display could not be refreshed');
          }
        });
        this.cart.classList.toggle('is-empty', state.item_count === 0);
        // If the user closed an already open drawer during the request, refresh
        // its content without letting the native Add renderer reopen it.
        if (this.cart.matches('cart-drawer') && this.cartWasOpen && !this.cart.classList.contains('active')) {
          this.cart.getSectionsToRender().forEach((section) => {
            const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
            const source = new DOMParser().parseFromString(state.sections[section.id], 'text/html')
              .querySelector(section.selector || '.shopify-section');
            if (target) target.innerHTML = source.innerHTML;
          });
          this.cart.querySelector('#CartDrawer-Overlay').addEventListener('click', () => this.cart.close());
        } else {
          CartPerformance.measure('add:paint-updated-sections', () => this.cart.renderContents(state));
        }
      }

      refreshCartDisplay(duringMutation = false) {
        if (this.pendingAddPromise && !duringMutation) return this.pendingAddPromise;
        if (this.cartRefreshPromise) return this.cartRefreshPromise;
        if (!this.pendingCartState || !this.cart) return Promise.resolve(false);
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.recoveryButton?.setAttribute('aria-disabled', 'true');
        if (this.recoveryButton) this.recoveryButton.textContent = 'Refreshing cart…';
        const sectionIds = this.cart.getSectionsToRender().map((section) => section.id);
        const sectionsUrl = new URL(routes.cart_url, window.location.origin);
        sectionsUrl.searchParams.set('sections', sectionIds.join(','));

        this.cartRefreshPromise = Promise.all([
          window.WorthItCartRequest(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' }),
          // Keep JSON negotiation on /cart.js only; this is a page section GET.
          window.WorthItCartRequest(sectionsUrl.toString(), { cache: 'no-store' }),
        ]).then(async ([cartResult, sectionsResult]) => {
          if (!cartResult.response.ok || !sectionsResult.response.ok) throw new Error('The cart display is unavailable');
          const cart = cartResult.data;
          const sections = sectionsResult.data;
          if (typeof cart?.currency !== 'string' || !cart.currency || !Array.isArray(cart.items) || !Number.isInteger(cart.item_count) || cart.item_count < 0 ||
              !cart.items.every((line) => typeof line.key === 'string' && line.key && Number.isInteger(line.quantity) && line.quantity > 0) ||
              cart.items.reduce((count, line) => count + line.quantity, 0) !== cart.item_count) {
            throw new Error('Unrecognised cart response');
          }
          // A notification refers to the complete Shopify line key, never a
          // guessed variant match (properties and selling plans can differ).
          if (this.cart.matches('cart-notification') && !cart.items.some((line) => line.key === this.pendingCartState.key)) {
            throw new Error('The added line is no longer in this cart');
          }
          await this.renderCartDisplay({ ...cart, id: this.pendingCartState.id, key: this.pendingCartState.key, sections });
          window.WorthItCartCheckout.confirm(this, cart);
          const variantId = this.pendingCartState.id;
          this.pendingCartState = null;
          if (ProductForm.pendingCartRefresh === this) ProductForm.pendingCartRefresh = null;
          this.recoveryNotice?.remove();
          this.recoveryNotice = null;
          this.submitButton.removeAttribute('aria-disabled');
          if (this.pendingCartWarning) this.handleErrorMessage(this.pendingCartWarning);
          if (this.pendingCartOutcome === 'quantity-warning' || this.pendingCartOutcome === 'unconfirmed') this.showCartQuantityWarning();
          this.publishCartUpdate(cart, variantId);
          return true;
        }).catch((error) => {
          console.error(error);
          this.showCartRecovery();
          return false;
        }).finally(() => {
          this.recoveryButton?.removeAttribute('aria-disabled');
          if (this.recoveryButton) this.recoveryButton.textContent = 'Refresh cart display';
          this.cartRefreshPromise = null;
        });
        return this.cartRefreshPromise;
      }

      showCartRecovery() {
        window.WorthItCartCheckout.uncertain(this);
        this.submitButton.setAttribute('aria-disabled', 'true');
        if (this.recoveryNotice?.isConnected) return;
        const notice = document.createElement('section');
        notice.className = 'product-form__cart-recovery';
        notice.setAttribute('aria-label', 'Cart update');
        notice.style.cssText = 'position:fixed;bottom:calc(80px + env(safe-area-inset-bottom));right:16px;width:min(440px,calc(100vw - 32px));box-sizing:border-box;padding:16px;background:#fff;color:#24152b;border:2px solid #432351;border-radius:16px;box-shadow:0 8px 32px #24152b26;z-index:120;font:inherit;';
        const message = document.createElement('p');
        message.setAttribute('role', 'status');
        message.style.cssText = 'margin:0 0 12px;line-height:1.5;';
        message.textContent = this.pendingCartOutcome === 'quantity-warning' ? this.pendingCartWarning :
          this.pendingCartOutcome === 'unconfirmed' ? 'We could not confirm this addition. Refresh the display or view your cart before trying again.' :
          'Your item was added, but this page could not refresh the cart. Check your cart before adding again.';
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'button button--secondary';
        retry.style.cssText = 'min-height:44px;min-width:44px;margin:0 8px 8px 0;';
        retry.textContent = 'Refresh cart display';
        retry.addEventListener('click', () => this.refreshCartDisplay());
        const link = document.createElement('a');
        link.href = routes.cart_url;
        link.className = 'button';
        link.style.cssText = 'min-height:44px;min-width:44px;margin-bottom:8px;';
        link.textContent = 'View cart';
        notice.append(message, retry, link);
        const drawer = document.querySelector('cart-drawer');
        (drawer?.classList.contains('active') ? document.getElementById('CartDrawer') : document.body).appendChild(notice);
        this.recoveryNotice = notice;
        this.recoveryButton = retry;
        // Quick Add removes this form while closing; keep its recovery reachable.
        if (drawer?.classList.contains('active')) trapFocus(document.getElementById('CartDrawer'), retry);
        else if (!this.isConnected) retry.focus();
      }

      showCartQuantityWarning() {
        // A Quick Add form may be removed or hide inline errors. Keep the
        // quantity warning with the reconciled cart so it remains visible.
        const scope = this.cart.querySelector('.drawer__inner') || this.cart.querySelector('#cart-notification') || this.cart;
        scope.querySelector('.product-form__quantity-warning')?.remove();
        const warning = document.createElement('p');
        warning.className = 'product-form__quantity-warning';
        warning.setAttribute('role', 'alert');
        warning.style.cssText = 'margin:12px 0;padding:12px;border:1px solid #432351;border-radius:8px;line-height:1.5;';
        warning.textContent = this.pendingCartWarning;
        scope.prepend(warning);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      async resolveCartLinesUpdate(deferred) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        let confirmedCart = null;
        try {
          // This projection belongs to this accepted Add. Do not reuse an older
          // aggregate request while a later mutation is waiting for its event.
          const { response, data: cart } = await window.WorthItCartRequest(`${routes.cart_url}.js`, {
            method: 'GET', headers: { Accept: 'application/json' }, cache: 'no-store',
          });
          if (!response.ok) throw new Error('The cart event could not be refreshed');
          if (typeof cart?.currency !== 'string' || !cart.currency || !Array.isArray(cart.items) || !Number.isInteger(cart.item_count) || cart.item_count < 0 ||
              !cart.items.every((line) => typeof line.key === 'string' && line.key && Number.isInteger(line.quantity) && line.quantity > 0) ||
              cart.items.reduce((count, line) => count + line.quantity, 0) !== cart.item_count) {
            throw new Error('Unrecognised cart response');
          }
          confirmedCart = cart;
          if (deferred && CartLinesUpdateEvent) deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
        } catch (error) {
          // Mutation acceptance is unchanged; only the event projection failed.
          deferred?.reject(error);
          console.error(error);
        }
        return confirmedCart;
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
